import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { useCreateKioskDefaultScreenMutation } from '@occc/graphql';

import { DefaultScreensCreatePage } from './default-screens-create-page.component';

import { DefaultScreenFormValues } from '../../touchpoint.types';
import { routeCreator } from '../../touchpoint.router';
import {
  ALL_IMAGES_REQUIRED_MESSAGE,
  DEFAULT_SCREENS_CREATE_PAGE_TITLE,
  DEFAULT_SCREEN_CREATE_SUCCESS_MESSAGE,
  DEFAULT_SCREEN_CREATE_ERROR_MESSAGE,
} from '../../touchpoint.constants';

export const DefaultScreensCreatePageContainer = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createKioskDefaultScreen] = useCreateKioskDefaultScreenMutation();

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.defaultScreensList(),
        label: 'Touchpoint',
      },
      {
        key: routeCreator.defaultScreensCreate(),
        label: DEFAULT_SCREENS_CREATE_PAGE_TITLE,
      },
    ],
    [],
  );

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback(
    async (values: DefaultScreenFormValues) => {
      setIsSubmitting(true);

      try {
        // Kept out of the zod schema deliberately: the schema is shared with the edit
        // page, and making the form invalid there would disable Save and suppress the
        // snackbar that OCST-28367 requires. (OCST-28367)
        if (!values.landscapeImageUrl || !values.squareImageUrl || !values.portraitImageUrl) {
          snackbar.error(ALL_IMAGES_REQUIRED_MESSAGE);
          setIsSubmitting(false);
          return;
        }

        const imageSet = {
          landscape: values.landscapeImageUrl,
          square: values.squareImageUrl,
          portrait: values.portraitImageUrl,
        };

        const { data } = await createKioskDefaultScreen({
          variables: {
            input: {
              name: values.name.trim(),
              description: values.description.trim(),
              imageSet,
              kioskIds: values.kioskIds || [],
            },
          },
          refetchQueries: ['kioskDefaultScreens'],
        });

        if (data?.createKioskDefaultScreen) {
          snackbar.success(DEFAULT_SCREEN_CREATE_SUCCESS_MESSAGE);
          navigate(routeCreator.defaultScreensList());
        } else {
          snackbar.error(DEFAULT_SCREEN_CREATE_ERROR_MESSAGE);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error creating default screen:', error);
        snackbar.error(DEFAULT_SCREEN_CREATE_ERROR_MESSAGE);
        setIsSubmitting(false);
      }
    },
    [createKioskDefaultScreen, navigate],
  );

  return (
    <DefaultScreensCreatePage
      breadcrumbLinks={breadcrumbLinks}
      onSubmit={onSubmit}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
    />
  );
};
